import {buildLsObjForBackend} from '@/services/build-object-to-backend.js';
import {DateTime} from 'luxon';
import {showGlobalBanner} from '@/services/global-banner.js';
import {trackEvent} from '@/services/tracking.js';
import {parseHTML} from '@/util/parseHTML.js';
import priceFormatter from '@/mixins/priceFormatter';

export default {
	data: () => ({
		isSavingWorkflow: false,
		saveTimerIsDone: false,
		workflowIsSaved: false,
		saveBtnAttr: {
			state: 'save', // save, saving, saved
			text: 'save',
			style: {'pointer-events': 'auto'},
		},
		csvProcessedWorkFlowSteps: [],
	}),
	mixins: [priceFormatter],
	watch: {
		workflowIsSaved() {
			this.saveWatcher();
		},
		saveTimerIsDone() {
			this.saveWatcher();
		},
	},
	computed: {
		getCountry() {
			return this.$store.state.jwt ? this.$store.state.jwt.country : this.$store.state.COUNTRY;
		},
		getUserName() {
			const jwt = this.$store.state.jwt;
			if (!jwt) return '';
			if (jwt.lastName && jwt.firstName) return `${jwt.lastName}, ${jwt.firstName}`;
			if (jwt.email) return jwt.email;
		},
		wfApiInfo() {
			const activeStepTags = {...this.$store.getters['tags/activeStepTags']};
			const activeWorkflowTags = {...this.$store.getters['tags/activeTags']};
			const tagsPublicationId = this.$store.state.tags.tagsPublicationId;
			let activeStepTagsObj = Object.keys(activeStepTags).reduce((acc, stepKey) => {
				acc[stepKey] = Object.keys(activeStepTags[stepKey]).reduce((acc_g, group) => {
					acc_g[group] = [...activeStepTags[stepKey][group]];
					return acc_g;
				}, {});
				return acc;
			}, {});
			let activeworkflowTagsObj = Object.keys(activeWorkflowTags).reduce((acc, group) => {
				acc[group] = [...activeWorkflowTags[group]];
				return acc;
			}, {});
			return {
				id: this.$store.state.workflowID,
				title: this.$store.state.workflowHeadline || this.$store.state.workflowDefaultHeadline,
				steps: this.$store.getters['workflow/wfSteps'],
				owner: this.$store.state.jwt?.email ?? '',
				analyte: this.analyte,
				application: this.application,
				startingMaterial: this.startingMaterial,
				technology: this.technology,
				activeStepTags: activeStepTagsObj,
				activeWorkflowTags: activeworkflowTagsObj,
				tagsPublicationId
			};
		},
	},
	methods: {
		saveWatcher() {
			if (this.workflowIsSaved && this.saveTimerIsDone) {
				this.endSaveBtnAnimation();
			}
		},
		setSaveBtnAttr(saveState) {
			this.saveBtnAttr.state = saveState; // show the correct icon based on save state
			this.saveBtnAttr.text = saveState;
			if (saveState === 'saving') {
				this.saveBtnAttr.style = {'pointer-events': 'none'}; // disable mouse click
			} else if (saveState === 'save') {
				this.saveBtnAttr.style = {'pointer-events': 'auto'}; // enable mouse click
			}
		},
		saveBtnError() {
			// resets save btn to original state
			this.setSaveBtnAttr('save');
			this.workflowIsSaved = false;
			this.saveTimerIsDone = false;
		},
		startSaveBtnAnimation() {
			this.setSaveBtnAttr('saving');
			setTimeout(() => {
				if (this.workflowIsSaved) {
					this.setSaveBtnAttr('saved');
				}
				this.saveTimerIsDone = true;
			}, 2000);
		},
		endSaveBtnAnimation() {
			this.setSaveBtnAttr('saved');
			// trigger snackbar
			this.$root.$emit('saveWorkflowSuccess');
			setTimeout(() => {
				this.saveBtnError();
			}, 6000);
		},
		checkIfLoggedIn() {
			// if jwt then user is logged in
			return !!this.$store.state.jwt;
		},
		isJwtExpired() {
			const expTime = this.$store.state.jwt?.exp;
			return expTime < new Date().getTime() / 1000;
		},
		async saveWorkflowInit() {
			if (this.checkIfLoggedIn() && !this.isJwtExpired()) {
				await this.saveForLoggedInUser();
			} else {
				this.resetJwtCookie();
				this.preLoginModal = true;
			}
			trackEvent('ctaClick', {eVar58: 'Save workflow | Workflow Configurator'});
		},
		async saveForLoggedInUser() {
			// remove localStorage item
			localStorage.removeItem('saveOnReturn');

			// set timestamp of saved workflow in $store for use by pdf header
			const now = new Date();
			const lastModDate = now.toLocaleDateString();
			this.$store.commit('setLastModDate', lastModDate);

			const workflowsEndpoint = this.$store.state.workflowsEndpoint;
			this.startSaveBtnAnimation();
			const wfJSON = buildLsObjForBackend(this.wfApiInfo, this.$store.state.hcube.publication_id);

			const workflowId = (await this.checkForSavedWorkflowID()) || (await this.$store.state.workflowID);
			function saveWFToLs(wf) {
				let lsWorkflowsRaw = localStorage.getItem('lsMyWorkflows');
				const workflow = {id: wf.id, title: wf.title, data: wf.document};
				let workflows = null;
				if (!lsWorkflowsRaw) {
					// Add a temp array for case user didn't go to my list page
					workflows = [workflow];
				} else {
					workflows = JSON.parse(lsWorkflowsRaw)?.workflows;
					const savedIndex = workflows.findIndex((lsWf) => lsWf.id === wf.id);
					workflows[savedIndex] = workflow;
				}

				localStorage.setItem('lsMyWorkflows', JSON.stringify({workflows}));
			}
			const resolveRequest = (postDataToBE) => {
				this.$store.commit('setWorkFlowId', postDataToBE.id);
				this.$store.commit('setWorkflowHeadline', postDataToBE.title);
				saveWFToLs(postDataToBE);
				this.saveWorkflowSuccess();
			};
			const rejectRequest = () => {
				showGlobalBanner(this, {
					title: 'Experiment could not be saved.',
					body: 'Due to technical reasons, we were unable to save this experiment. Please try again.',
					btn: 'dismiss',
				});
			};
			// if ID is set then send as PUT else POST to api
			if (workflowId !== null) {
				this.callToApiSave(`${workflowsEndpoint}/${workflowId}`, 'put', wfJSON)
					.then((data) => resolveRequest(data))
					.catch(() => rejectRequest(this));
			} else {
				this.callToApiSave(`${workflowsEndpoint}`, 'post', wfJSON)
					.then((data) => resolveRequest(data))
					.catch(() => rejectRequest(this));
			}
		},
		async checkForSavedWorkflowID() {
			const lsWorkflowID = localStorage.getItem('lsWorkflowID');
			if (!lsWorkflowID) return false;
			await this.$store.commit('setWorkFlowId', +lsWorkflowID);
			localStorage.removeItem('lsWorkflowID');
			return +lsWorkflowID;
		},

		saveWorkflowSuccess() {
			this.workflowIsSaved = true;
			this.isSavedWorkflow = true;
			this.$store.state.isSavedWorkflow = true;
		},
		downloadBlob(blob, filename) {
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');

			link.href = url;
			link.download = filename || 'download';

			const clickHandler = function () {
				setTimeout(() => {
					URL.revokeObjectURL(url);
					this.removeEventListener('click', clickHandler);
					(this.remove && (this.remove(), 1)) || (this.parentNode && this.parentNode.removeChild(this));
				}, 150);
			};

			return link.click();
		},
		CSVFileName(fileName) {
			const name = fileName || this.$store.state.workflowHeadline || this.$store.state.workflowDefaultHeadline;
			return `WFC_${name}.csv`;
		},
		parseStringSW(content) {
			let parsedContent = parseHTML(content, true);
			parsedContent = parsedContent.replace(/\"/g, '""');
			return `\"${parsedContent}\"`;
		},
		parseProductNameSW(product, countryCode) {
			let productName = '';
			if (product && product.hasOwnProperty('customContent')) {
				productName = product?.customContent.title;
			} else {
				productName = product?.name[countryCode];
			}

			if (!productName) return '';

			return this.parseStringSW(productName);
		},
		parseDescriptionSW(product, countryCode) {
			const productDescription = product.hasOwnProperty('customContent')
				? product.customContent.desc
				: product.description;
			if (productDescription) {
				productDescription.replace(';', ',');
				return this.parseStringSW(productDescription);
			}
			return '';
		},
		searchObjectSW(searchedObject, searchedValue) {
			for (const key in searchedObject) if (key === searchedValue) return searchedObject[key];
		},
		parsePriceSW(selectedProduct, pricesStatusesArray, currentCountryCode) {
			if (
				selectedProduct.itemType === 'GeneGlobe' ||
				!selectedProduct.prices ||
				!currentCountryCode ||
				!pricesStatusesArray
			)
				return '';

			const priceStatus = this.searchObjectSW(pricesStatusesArray, currentCountryCode);
			if (priceStatus !== 'Display' && priceStatus !== 'Login') return '';

			const [currentCountryPrice] = selectedProduct.prices.filter(
				(price) => price.countryIsoCode === currentCountryCode,
			);
			const {price} = currentCountryPrice;

			if (price && price.custom) {
				return `\"${this.formatPrice(price.custom, price.currencyIsoCode)}\"`;
			}
			if (price && price.value) {
				return `\"${this.formatPrice(price.value, price.currencyIsoCode)}\"`;
			}
			return '';
		},
		getStepNameSW(cvlWebSteps, workflowKey, language) {
			if (cvlWebSteps) {
				const checkForName = cvlWebSteps.values.find((step) => step.key === workflowKey);
				const stepName = checkForName ? checkForName.value[language] : workflowKey;
				return this.parseStringSW(stepName.replace(/\n|,/g, ' ').trim());
			}
			return '';
		},
		getCatalogNumberSW(product) {
			return product?.catalogNumber ? this.parseStringSW(product.catalogNumber) : '';
		},
		findSelectedProductsSW({products, selected}) {
			return products.filter((product) => selected.some(({updated}) => updated.includes(product.key)));
		},
		downloadCSV(wfTitle) {
			const fileName = this.CSVFileName(wfTitle);
			trackEvent('ctaClick', {eVar58: 'Download (CSV) | Workflow Configurator'});
			const date = DateTime.fromISO(new Date().toISOString()).toFormat('dd.MM.yyyy');
			const time = new Date().toLocaleTimeString('en-US', {
				hour: '2-digit',
				minute: '2-digit',
				hour12: true,
			});

			trackEvent('downloadCsv', {eVar23: `${fileName} | csv`});

			const rows = [
				['Date', date],
				['Time', time],
				['Owner', `\"${this.getUserName}\"`.replace(/\n|,/g, ' ')],
				['Step number', 'Step name', 'Product name', 'Cat Number', 'Product Description', 'Price'],
			];

			this.csvProcessedWorkFlowSteps = this.renderWorkFlowSteps
				.filter((workflowStep) => workflowStep.stepEnabled)
				.reduce((accumulator, step, index) => {
					let currentIndex = ++index;
					const {name, products, customContent} = step;

					if (customContent) {
						accumulator.push([
							currentIndex,
							this.getStepNameSW(this.$store.state.cvlWebSteps, name, this.$store.state.LANG),
							null,
							null,
							null,
							null,
						]);
					} else if (products) {
						const selectedProducts = this.findSelectedProductsSW(step);
						const result = selectedProducts.map((selectedProduct) => {
							return [
								currentIndex,
								this.getStepNameSW(this.$store.state.cvlWebSteps, name, this.$store.state.LANG),
								this.parseProductNameSW(selectedProduct, this.$store.state.LANG),
								this.getCatalogNumberSW(selectedProduct),
								this.parseDescriptionSW(selectedProduct, this.$store.state.LANG),
								this.parsePriceSW(selectedProduct, selectedProduct.priceStatuses, this.getCountry),
							];
						});
						result.forEach((product) => accumulator.push(product));
					} else {
						accumulator.push([
							++currentIndex,
							this.getStepNameSW(this.$store.state.cvlWebSteps, name, this.$store.state.LANG),
						]);
					}
					return accumulator;
				}, []);

			let csvContent = '';

			[...rows, ...this.csvProcessedWorkFlowSteps].forEach((currentRow) => {
				const row = currentRow.join(',');
				csvContent += row + '\r\n';
			});

			const blob = new Blob(['\uFEFF' + csvContent], {encoding: 'UTF-8', type: 'data:text/csv;charset=UTF-8'});
			this.downloadBlob(blob, fileName);
		},
		async downloadPDF() {
			trackEvent('ctaClick', {eVar58: 'Download (PDF) | Workflow Configurator'});
			trackEvent('downloadPdf', {eVar23: 'QIAGEN - Workflow Configurator | pdf'});
			let imageRefStrings = [];
			const self = this;
			const beforePrint = function () {
				let index = 0;
				for (const step of self.renderWorkFlowSteps) {
					const wfstep = document.getElementById(`${index + 1}-wfstep`);
					const wfheader = document.getElementById(`${index + 1}-wfheader`);

					// set attributes on step components for use by print media
					if (self.labNotesActive) wfstep.setAttribute('data-eln', 'active');
					else wfstep.setAttribute('data-eln', 'inactive');

					// set attributes on step headers for use by print media
					if (step.stepEnabled == true) wfheader.setAttribute('data-print', 'enabled');
					else wfheader.setAttribute('data-print', 'disabled');

					// remove style refs from images to allow print media to use the image src url
					if (!step.hasOwnProperty('customContent')) {
						for (const selected of step.selected) {
							let imageId = `${index + 1}-wfimage-${selected.updated}`;
							const wfimage = document.getElementById(imageId);
							if (wfimage !== null) {
								let url = wfimage.getAttribute('src');
								let refString = wfimage.getAttribute('style');
								wfimage.style = '';
								imageRefStrings.push({
									imageId: imageId,
									imageRefString: refString,
								});
							}
						}
					}
					index++;
				}
			};
			const afterPrint = function () {
				// restore style refs to images to allow swap action at $mobile breakpoint
				imageRefStrings.forEach((ref) => {
					const wfimage = document.getElementById(ref.imageId);
					wfimage.setAttribute('style', ref.imageRefString);
				});
			};
			window.onbeforeprint = beforePrint;
			window.onafterprint = afterPrint;
			window.print();
		},
		updateSingleWorkflowAPI(method, pathId, wf) {
			const workflowsEndpoint = this.$store.state.workflowsEndpoint;
			let wfJSON = '';
			if (wf) {
				wfJSON = JSON.stringify({
					document: wf.data,
					id: wf.id,
					title: wf.title,
					publication_id: wf.publication_id,
				});
			}
			const path = pathId ? `${workflowsEndpoint}/${pathId}` : `${workflowsEndpoint}`;
			return this.callToApiSave(path, method, wfJSON)
				.then((data) => data)
				.catch((err) => {
					throw err;
				});
		},
	},
};
